import useShopifyShopLocations from "@babackapp/private-api-client/client/use-shopify-shop-locations";
import type { ShopifyLocationDto } from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@babackapp/ui/components/command";
import { Label } from "@babackapp/ui/components/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@babackapp/ui/components/popover";
import { ChevronDown } from "lucide-react";
import { useEffect } from "react";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { useBoolean } from "@/hooks/use-boolean";

type ShopLocationSelectProps = {
  value: ShopifyLocationDto | null;
  onChange: (location: ShopifyLocationDto | null) => void;
  label?: string;
  description?: string;
  allowNone?: boolean;
};

export default function ShopLocationSelect({
  value,
  onChange: onChangeLocation,
  label,
  description,
  allowNone = true,
}: ShopLocationSelectProps) {
  const t = useTranslation();
  const open = useBoolean(false);
  const { data: shop } = useCurrentShopSuspense();
  const { data: locations } = useShopifyShopLocations(shop.id);
  useEffect(() => {
    if (!shop.preferences.restockOnReturn) return;

    const prefId = shop.preferences.restockLocationId;
    const matched =
      locations?.find((location) => location.id === prefId) ?? null;
    if (matched) {
      onChangeLocation(matched);
      return;
    }

    if (locations?.length) {
      onChangeLocation(locations[0]);
    }
  }, [
    shop.preferences.restockLocationId,
    shop.preferences.restockOnReturn,
    locations,
    allowNone,
  ]);

  const onSelect = (value: ShopifyLocationDto | null) => () => {
    onChangeLocation(value);
    open.setFalse();
  };

  return (
    <Popover open={open.value} onOpenChange={open.setValue} modal={true}>
      <div>
        <div>
          <Label>{label ?? t("carrier.locations.restockRules")}</Label>
          <p className="my-0 py-1 max-w-[350px] pb-2 text-sm text-muted-foreground">
            {description ?? t("carrier.locations.chooseLocationDescription")}
          </p>
        </div>
        <div className="flex justify-start">
          <PopoverTrigger asChild>
            <Button variant="secondary" size="sm" className="mb-6">
              {value
                ? `${value.name}`
                : allowNone
                  ? "Don't restock"
                  : "Select a location"}{" "}
              <ChevronDown className="ml-2 size-4" />
            </Button>
          </PopoverTrigger>
        </div>
      </div>
      <PopoverContent className="p-0">
        <Command
          value={undefined}
          filter={(value, search) => {
            const match = value.toLowerCase().includes(search.toLowerCase());
            return match ? 1 : 0;
          }}
        >
          <CommandInput />
          <CommandList>
            <CommandEmpty>
              {t("carrier.locations.noLocationFound")}
            </CommandEmpty>

            {locations?.map((location) => (
              <CommandItem
                value={location.name}
                className="p-3 font-medium"
                key={location.id}
                onSelect={onSelect(location)}
              >
                {location.name}
              </CommandItem>
            ))}
            {allowNone && (
              <CommandItem
                className="p-3 font-medium"
                onSelect={onSelect(null)}
                value="-"
              >
                {t("carrier.locations.dontRestock")}
              </CommandItem>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
